<script setup lang="ts">
import BreakpointIndicator from '@/components/breakpoint-indicator.vue';
import WhatsNewModal from '@/components/doc-ai/doc-search/whats-new/whats-new-modal.vue';
import MaintenanceModal from '@/components/maintenance-modal.vue';
import { useAppTitle } from '@/composables/use-app-title.ts';
import { useWhatsNew } from '@/composables/use-whats-new.ts';
import { useLaunchDarklyContext } from '@/composables/user/use-launchdarkly-context.ts';
import { useUserProfile } from '@/composables/user/use-user-profile.ts';
import { useAuthStore } from '@/stores/auth-store.ts';
import { Notification, Notivue, NotivueSwipe } from 'notivue';
import { computed, watch } from 'vue';
import { RouterView } from 'vue-router';

/* ---------------------------------------------------------------------------------------------- */
/*
 * Description: Vue app's bootstrap sfc
 */

useAppTitle('Home');

const isDev = computed(() => import.meta.env.DEV);

/* ---------------------------------------------------------------------------------------------- */
/**
 * fetch user profile on every page refresh if the user is authenticated
 */

const authStore = useAuthStore();
const { fetchUserProfile } = useUserProfile();
const { watchAuthState } = useLaunchDarklyContext();

watch(() => authStore?.isAuthenticated, async (isAuthenticated) => {
   try {
      if (isAuthenticated) {
         await fetchUserProfile(true);
      }
   }
   catch (error) {
      console.error(error);
   }
}, { immediate: true });

// Sync LaunchDarkly context with user authentication state
watchAuthState();

/* ---------------------------------------------------------------------------------------------- */

// What's New popup - shows automatically on first visit after a new release
const { isOpen: isWhatsNewOpen, close: closeWhatsNew } = useWhatsNew();

/* ---------------------------------------------------------------------------------------------- */
</script>

<template>
   <!-- Maintenance modal (shows once per login) -->
   <MaintenanceModal />

   <!-- What's New Modal - shows automatically on first visit after a new release -->
   <WhatsNewModal
      v-if="authStore.isAuthenticated"
      v-model="isWhatsNewOpen"
      @update:model-value="(val) => !val && closeWhatsNew()"
   />

   <RouterView v-slot="{ Component }">
      <template v-if="Component">
         <Transition mode="out-in">
            <Suspense>
               <!-- main content -->
               <component :is="Component"></component>

               <!-- loading state -->
               <template #fallback>
                  Loading...
               </template>
            </Suspense>
         </Transition>

         <!-- notivue toast -->
         <Notivue v-slot="item">
            <NotivueSwipe :item="item">
               <Notification :item="item" />
            </NotivueSwipe>
         </Notivue>
      </template>
   </RouterView>

   <BreakpointIndicator v-if="isDev" />
</template>

<style scoped>
</style>
